import type { AccountId, RoundTrip, SessionSummary } from "@trader/shared/Money"
import { BigDecimal } from "effect"
import { formatMoney, formatSignedMoney, formatTime, toneOf } from "./format.ts"

// What the session summary shows (docs/design/0008): its tiles, span and trade
// rows, formatted from the server's summary. The server does the money math;
// this only formats it (ratios like win rate arrive as numbers). Plain TS, no React.

export interface SummaryTile {
  readonly label: string
  readonly value: string
  /** "up" / "down" / "" (the value's colour). */
  readonly tone: string
  readonly sub?: string
  readonly title?: string
}

const ratioFormat = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const signedPercentFormat = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  signDisplay: "exceptZero"
})

/** A ratio such as a profit factor: "1.85"; "—" when undefined. */
export const formatRatio = (value: number | null): string =>
  value === null || !Number.isFinite(value) ? "—" : ratioFormat.format(value)

/** A share from 0 to 1 as a percent: 0.625 → "62.50%"; "—" when undefined. */
export const formatShare = (value: number | null): string =>
  value === null || !Number.isFinite(value) ? "—" : `${ratioFormat.format(value * 100)}%`

/** A signed percent the server computed: 1.234 → "+1.23%". */
export const formatSignedPercent = (value: number): string =>
  Number.isFinite(value) ? `${signedPercentFormat.format(value)}%` : "—"

/** The tone of an amount shown as a loss whatever its sign (charges, drawdown): red unless zero. */
const lossTone = (value: BigDecimal.BigDecimal): string => (BigDecimal.isZero(value) ? "" : "down")

/** The KPI tiles, in reading order. */
export const summaryTiles = (summary: SessionSummary): ReadonlyArray<SummaryTile> => {
  const currency = summary.currency
  const stats = summary.stats
  const money = (value: BigDecimal.BigDecimal | null) => formatMoney(value, currency)
  const signed = (value: BigDecimal.BigDecimal | null) => formatSignedMoney(value, currency)
  const ended = summary.endedAt !== null
  return [
    {
      label: "Net P&L",
      value: signed(summary.netPnl),
      tone: toneOf(summary.netPnl),
      sub: formatSignedPercent(summary.netPnlPct),
      title: "Ending equity minus starting cash, after charges"
    },
    { label: "Ending equity", value: money(summary.endingEquity), tone: "", title: "Cash plus open positions at their marks" },
    { label: "Starting cash", value: money(summary.startingCash), tone: "" },
    { label: "Realized P&L", value: signed(summary.realizedPnl), tone: toneOf(summary.realizedPnl), title: "Before charges" },
    {
      label: "Unrealized P&L",
      value: signed(summary.unrealizedPnl),
      tone: toneOf(summary.unrealizedPnl),
      sub: ended ? "at the final prices" : "at current prices",
      title: "Open positions, valued at their last price"
    },
    { label: "Charges", value: money(summary.charges), tone: lossTone(summary.charges) },
    {
      label: "Trades",
      value: String(stats.trades),
      tone: "",
      sub: `${stats.wins} won · ${stats.losses} lost`,
      title: "Closed round trips"
    },
    { label: "Win rate", value: formatShare(stats.winRate), tone: "" },
    {
      label: "Profit factor",
      value: formatRatio(stats.profitFactor),
      tone: "",
      title: "Gross profit divided by gross loss (none without a losing trade)"
    },
    { label: "Avg win", value: signed(stats.avgWin), tone: toneOf(stats.avgWin) },
    { label: "Avg loss", value: signed(stats.avgLoss), tone: toneOf(stats.avgLoss) },
    { label: "Largest win", value: signed(stats.largestWin), tone: toneOf(stats.largestWin) },
    { label: "Largest loss", value: signed(stats.largestLoss), tone: toneOf(stats.largestLoss) },
    {
      label: "Max drawdown (closed trades)",
      value: money(stats.maxDrawdown),
      tone: lossTone(stats.maxDrawdown),
      sub: formatShare(stats.maxDrawdownPct),
      title: "Largest fall of starting cash plus the closed trades' net P&L from its peak"
    }
  ]
}

/** "02 Jan 2024, 09:15 → 05 Jan 2024, 15:30" in Indian time. */
export const sessionSpan = (summary: SessionSummary): string => `${formatTime(summary.from)} → ${formatTime(summary.to)}`

/** Whether the session is over, in words. */
export const sessionState = (summary: SessionSummary): string =>
  summary.endedAt === null
    ? "Running: open positions are valued at current prices"
    : `Ended ${formatTime(summary.endedAt)}: read-only, open positions valued at the final prices`

export interface TradeRow {
  /** 1 for the first trade closed. */
  readonly number: number
  readonly key: string
  readonly trade: RoundTrip
}

/** The trades newest first (the server sends oldest exit first), numbered in closing order. */
export const tradeRows = (trades: ReadonlyArray<RoundTrip>): ReadonlyArray<TradeRow> =>
  trades
    .map((trade, i) => ({ number: i + 1, key: `${i}:${trade.instrumentId}:${trade.product}:${trade.exitTime}`, trade }))
    .reverse()

// --- The dialog shown after End ------------------------------------------------------------

/** Which account's summary the dialog shows (null: closed). One per app: End can come from the panel or the replay strip. */
const createSummaryDialog = () => {
  let accountId: AccountId | null = null
  const listeners = new Set<() => void>()
  const set = (next: AccountId | null) => {
    if (next === accountId) return
    accountId = next
    for (const listener of listeners) listener()
  }
  return {
    get: (): AccountId | null => accountId,
    open: (id: AccountId): void => set(id),
    close: (): void => set(null),
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    }
  }
}

export const summaryDialog = createSummaryDialog()
